import { useEffect, useRef, useState } from 'react';
import { flushAll } from '../state/saves';
import { useSw } from './swRegistration';

/**
 * 새로고침: 입력 중인 내용을 먼저 저장하고, 새 버전이 있으면 그 버전으로, 없으면 그냥 다시 연다.
 * (설치한 앱에는 브라우저 새로고침 버튼이 없어서 앱을 껐다 켜야 했다)
 */
export async function refreshApp(): Promise<void> {
  await flushAll().catch(() => undefined);
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
    if (reg?.waiting) {
      // 새 버전이 받아졌으면 적용과 함께 새로고침
      await new Promise((r) => setTimeout(r, 300));
      const update = useSw.getState().update;
      if (update) return void (await update(true));
    }
  } catch {
    // 오프라인 등: 그냥 다시 연다
  }
  window.location.reload();
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

/** 맨 위에서 끌어내리면 새로고침 (설치한 앱에서만. 브라우저 탭은 브라우저 자체 기능을 쓴다) */
export function usePullToRefresh(threshold = 80): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<number | null>(null);
  const pullRef = useRef(0);

  useEffect(() => {
    if (!isStandalone()) return;
    const scrolledInside = (el: EventTarget | null) => {
      for (let n = el as HTMLElement | null; n && n !== document.body; n = n.parentElement) {
        if (n.matches?.('input, textarea, select, [contenteditable="true"]')) return true;
        if (n.scrollTop > 0) return true;
      }
      return false;
    };
    const onStart = (e: TouchEvent) => {
      start.current = window.scrollY <= 0 && e.touches.length === 1 && !scrolledInside(e.target) ? e.touches[0]!.clientY : null;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const dy = e.touches[0]!.clientY - start.current;
      if (dy <= 0 || window.scrollY > 0) {
        pullRef.current = 0;
        setPull(0);
        return;
      }
      pullRef.current = Math.min(dy * 0.5, threshold * 1.5);
      setPull(pullRef.current);
    };
    const onEnd = () => {
      if (start.current !== null && pullRef.current >= threshold) {
        setRefreshing(true);
        void refreshApp();
      } else setPull(0);
      start.current = null;
      pullRef.current = 0;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [threshold]);

  return { pull: refreshing ? threshold : pull, refreshing };
}
